import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Carousel from "react-multi-carousel";
import { GoCalendar } from "react-icons/go";
import { FaMapMarkerAlt, FaExclamationCircle } from "react-icons/fa";
import "react-multi-carousel/lib/styles.css";

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 1,
    slidesToSlide: 1,
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 1,
    slidesToSlide: 1,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

const FlashAlertDetails = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const location = useLocation();
  const id = location.pathname.split("/")[2];
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${BASE_URL}/flash-alerts/${id}/details/`);
        if (!response.ok) throw new Error("Failed to fetch flash alert details");
        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching flash alert details:", error);
      }
    };
    fetchData();
  }, [id]);

  if (!data) return <p>Loading...</p>;

  const images = Array.isArray(data.images) ? data.images : [];

  return (
    <div className="bg-[#FFFFFF] rounded-lg shadow-md border border-[#E5E7EB] overflow-hidden">
      {/* Header with Carousel */}
      <div className="bg-[#F9FAFB] border-b border-[#E5E7EB] relative">
        <div className="relative h-64 w-full overflow-hidden">
          <Carousel
            swipeable={true}
            draggable={false}
            showDots={true}
            responsive={responsive}
            ssr={true}
            infinite={true}
            autoPlay={true}
            autoPlaySpeed={3000}
            keyBoardControl={true}
            customTransition="all .5"
            transitionDuration={1000}
            containerClass="carousel-container"
            removeArrowOnDeviceType={["tablet", "mobile"]}
            dotListClass="custom-dot-list-style"
            itemClass="carousel-item-padding-40-px"
          >
            {images && images.length > 0 ? (
              images.map((image: { image_url: string; thumbnail_url?: string },
                index: number) => (
                <div
                  className="w-full h-64"
                  key={index}
                  style={{ minHeight: "256px" }}
                >
                  <img
                    src={image.thumbnail_url || image.image_url}
                    alt={`Flash alert image ${index}`}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              ))
            ) : (
              <div className="w-full h-64 flex items-center justify-center bg-gray-200 text-gray-500">
                No images available
              </div>
            )}
          </Carousel>

          {/* Gradient + Text Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#111827B3] to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-10">
            <h2 className="text-3xl font-semibold mb-2">{data?.title}</h2>
            <div className="flex items-center text-sm text-[#F3F4F6]">
              <div className="flex items-center">
                <FaMapMarkerAlt className="mr-1" />
                <span>{data?.location}</span>
              </div>
              <div className="mx-2 text-[#D1D5DB]">|</div>
              <div className="flex items-center">
                <GoCalendar className="mr-1" />
                <span>{data?.date}</span>
              </div>
              <div className="mx-2 text-[#D1D5DB]">|</div>
              <div className="flex items-center">
                <FaExclamationCircle className="mr-1" />
                <span
                  className={`font-medium ${data?.priority === "High"
                    ? "text-[#F87171]"
                    : data?.priority === "Medium"
                      ? "text-[#FBBF24]"
                      : "text-[#34D399]"
                    }`}
                >
                  {data?.priority} Priority
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-2">Description</h3>
          <p className="text-[#4B5563]">{data?.description}</p>
        </div>

        <div className="bg-[#F9FAFB] p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-[#1F2937] mb-2">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-[#6B7280]">Contact Person</p>
              <p className="text-[#374151]">{data?.added_by}</p>
            </div>
            <div>
              <p className="text-sm text-[#6B7280]">Email</p>
              <a
                href={`mailto:${data?.contactEmail}`}
                className="text-[#2563EB] hover:text-[#1D4ED8] transition-colors duration-200"
              >
                {data?.added_by}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashAlertDetails;
